import { Fragment, useState } from "react";
import { ChevronRight } from "lucide-react";
import { fmt, rd, FUNNEL_STEPS } from "./fniShared";

const A_COLOR = "#0891b2";
const B_COLOR = "#7c3aed";

function Delta({ a, b, kind }) {
  if (a == null || b == null) return <span className="text-gray-300">—</span>;
  const d = kind === "pct" ? Math.round((a - b) * 10) / 10 : a - b;
  const color = d > 0 ? "#059669" : d < 0 ? "#dc2626" : "#94a3b8";
  const txt = kind === "pct" ? `${d > 0 ? "+" : ""}${d} pp` : kind === "rd" ? `${d > 0 ? "+" : d < 0 ? "-" : ""}${rd(Math.abs(d))}` : `${d > 0 ? "+" : ""}${fmt(d)}`;
  return <span className="font-extrabold whitespace-nowrap" style={{ color }}>{txt}</span>;
}

const show = (v, kind) => (kind === "pct" ? `${v ?? 0}%` : kind === "rd" ? rd(v) : fmt(v));

export function CompareLegend({ a, b }) {
  return (
    <div className="flex flex-wrap gap-3 text-[12px] font-bold mb-4" data-testid="fni-compare-legend">
      <span className="flex items-center gap-1.5"><i className="w-2.5 h-2.5 rounded-full" style={{ background: A_COLOR }}/> A · {a}</span>
      <span className="flex items-center gap-1.5"><i className="w-2.5 h-2.5 rounded-full" style={{ background: B_COLOR }}/> B · {b}</span>
      <span className="text-gray-400">Cada campaña con todas sus fechas · Δ = A − B</span>
    </div>
  );
}

const EXEC = [["leads", "Leads"], ["oportunidad", "Oportunidades"], ["sometido", "Sometidas a banco"],
              ["approval_rate", "Tasa de aprobación", "pct"], ["en_espera", "En espera del banco"],
              ["facturado", "Facturado"], ["monto_sometido", "Monto sometido (est.)", "rd"],
              ["monto_aprobado", "Monto aprobado (est.)", "rd"]];

export function CompareExecutive({ a, b }) {
  return (
    <section className="grid gap-4 mb-5 lg:grid-cols-[1fr_420px]" data-testid="fni-compare-executive">
      <div className="grid gap-3 grid-cols-2 xl:grid-cols-4 auto-rows-fr">
        {EXEC.map(([k, label, kind]) => (
          <div key={k} className="surface p-4 min-w-0" data-testid={`fni-cmp-kpi-${k}`}>
            <div className="label-up text-[10px] mb-2">{label}</div>
            <div className="flex items-baseline gap-2 flex-wrap">
              <span className="font-display font-extrabold text-[24px] leading-none" style={{ color: A_COLOR }}>{show(a[k], kind)}</span>
              <span className="font-display font-extrabold text-[15px]" style={{ color: B_COLOR }}>vs {show(b[k], kind)}</span>
            </div>
            <div className="text-[12px] mt-1.5"><Delta a={a[k]} b={b[k]} kind={kind}/></div>
          </div>
        ))}
      </div>
      <div className="surface p-4" data-testid="fni-compare-funnel">
        <h3 className="font-display font-extrabold text-base mb-3">Embudo A vs B</h3>
        <table className="w-full text-[13px]">
          <thead>
            <tr className="text-[10px] uppercase tracking-wider text-gray-400 border-b">
              <th className="py-1 text-left font-extrabold">Paso</th>
              <th className="py-1 text-right font-extrabold" style={{ color: A_COLOR }}>A</th>
              <th className="py-1 text-right font-extrabold" style={{ color: B_COLOR }}>B</th>
              <th className="py-1 text-right font-extrabold">Δ</th>
            </tr>
          </thead>
          <tbody>
            {FUNNEL_STEPS.map((s) => (
              <tr key={s.key} data-testid={`fni-cmp-funnel-${s.key}`}>
                <td className={`py-0.5 ${s.indent ? "pl-4 text-gray-600 font-semibold" : "font-extrabold"}`}>{s.label}</td>
                <td className="py-0.5 text-right tabular-nums font-bold">{fmt(a[s.key])}</td>
                <td className="py-0.5 text-right tabular-nums font-bold text-gray-600">{fmt(b[s.key])}</td>
                <td className="py-0.5 text-right tabular-nums text-[12px]"><Delta a={a[s.key]} b={b[s.key]}/></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

const byName = (rows) => Object.fromEntries((rows || []).map((r) => [r.name, r]));

function Cells({ ra, rb, metrics }) {
  return metrics.map(([k, , kind]) => (
    <Fragment key={k}>
      <td className="py-1.5 px-1.5 text-right tabular-nums font-bold" style={{ color: A_COLOR }}>{show(ra?.[k] ?? 0, kind)}</td>
      <td className="py-1.5 px-1.5 text-right tabular-nums text-gray-600">{show(rb?.[k] ?? 0, kind)}</td>
      <td className="py-1.5 px-1.5 text-right tabular-nums text-[12px] border-r border-gray-100"><Delta a={ra?.[k] ?? 0} b={rb?.[k] ?? 0} kind={kind}/></td>
    </Fragment>
  ));
}

export function CompareTable({ title, first, rowsA, rowsB, metrics, expandable, testid }) {
  const [open, setOpen] = useState({});
  const A = byName(rowsA);
  const B = byName(rowsB);
  const names = [...new Set([...(rowsA || []).map((r) => r.name), ...(rowsB || []).map((r) => r.name)])];
  return (
    <section className="surface p-4 mb-5 min-w-0" data-testid={testid}>
      <h3 className="font-display font-extrabold text-base mb-3">{title} · A vs B</h3>
      <div className="overflow-x-auto">
        <table className="w-full text-[13px]">
          <thead>
            <tr className="text-[10px] uppercase tracking-wider text-gray-400">
              <th/>
              {metrics.map(([k, l]) => <th key={k} colSpan={3} className="py-1 px-1.5 text-center font-extrabold border-r border-gray-100">{l}</th>)}
            </tr>
            <tr className="text-[10px] uppercase tracking-wider text-gray-400 border-b">
              <th className="py-1 px-1.5 text-left font-extrabold">{first}</th>
              {metrics.map(([k]) => (
                <Fragment key={k}>
                  <th className="py-1 px-1.5 text-right" style={{ color: A_COLOR }}>A</th>
                  <th className="py-1 px-1.5 text-right" style={{ color: B_COLOR }}>B</th>
                  <th className="py-1 px-1.5 text-right border-r border-gray-100">Δ</th>
                </Fragment>
              ))}
            </tr>
          </thead>
          <tbody>
            {names.map((n, i) => {
              const subs = expandable ? [...new Set([...(A[n]?.marcas || []), ...(B[n]?.marcas || [])].map((m) => m.name))] : [];
              const mA = byName(A[n]?.marcas);
              const mB = byName(B[n]?.marcas);
              return (
                <Fragment key={n}>
                  <tr className="border-b border-gray-50" data-testid={`${testid}-row-${i}`}>
                    <td className="py-1.5 px-1.5 font-extrabold text-gray-900 whitespace-nowrap">
                      {expandable && (
                        <button className="inline-flex align-middle mr-1 text-gray-400" onClick={() => setOpen((o) => ({ ...o, [n]: !o[n] }))}
                                data-testid={`${testid}-row-${i}-toggle`}>
                          <ChevronRight size={14} className={`transition-transform ${open[n] ? "rotate-90" : ""}`}/>
                        </button>
                      )}
                      {n}
                    </td>
                    <Cells ra={A[n]} rb={B[n]} metrics={metrics}/>
                  </tr>
                  {open[n] && subs.map((s) => (
                    <tr key={s} className="border-b border-gray-50 bg-gray-50/60">
                      <td className="py-1.5 px-1.5 pl-7 text-gray-600 font-semibold">{s}</td>
                      <Cells ra={mA[s]} rb={mB[s]} metrics={metrics}/>
                    </tr>
                  ))}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}
